import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLight } from "@/context/LightContext";

/**
 * The luxury 3D activation switch.
 * Renders only until the user activates it; on click, emits a light-burst
 * pulse from its center and calls `activate(x, y)` with screen coords.
 */
export default function LightSwitch() {
    const { activated, activate } = useLight();
    const ref = useRef(null);
    const [burst, setBurst] = useState(null); // {x,y}

    const handleActivate = () => {
        if (activated) return;
        const rect = ref.current?.getBoundingClientRect();
        const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
        const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
        setBurst({ x, y });
        // Give the burst a beat before flipping the switch so the visual reads
        setTimeout(() => activate(x, y), 220);
    };

    return (
        <>
            <AnimatePresence>
                {!activated && (
                    <motion.div
                        key="switch"
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.1, transition: { duration: 0.4 } }}
                        transition={{ duration: 0.9, delay: 0.4 }}
                        className="relative z-30 flex flex-col items-center gap-6 py-10"
                        data-testid="light-switch-wrapper"
                    >
                        <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-[var(--accent-champagne)]/90 text-center">
                            Turn On the Light to See Your Revenue Potential.
                        </p>

                        <button
                            ref={ref}
                            data-testid="light-switch"
                            onClick={handleActivate}
                            aria-label="Turn on the light"
                            className="dial group relative w-32 h-32 md:w-36 md:h-36 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-4 focus-visible:ring-offset-[#2a0303]"
                        >
                            <span className="dial-glow" aria-hidden />
                            <span className="dial-ring" aria-hidden />
                            <span className="dial-face" aria-hidden>
                                <span className="dial-face-inner" aria-hidden />
                                <span className="dial-indicator" aria-hidden />
                                <span className="dial-dot" aria-hidden />
                            </span>
                            <span className="dial-shadow" aria-hidden />
                        </button>

                        <p className="text-[10px] uppercase tracking-[0.4em] text-[var(--text-secondary)]/70">
                            Click to illuminate
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Activation burst (one-shot) */}
            <AnimatePresence>
                {burst && (
                    <motion.div
                        key="burst"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: [0, 1, 0.6, 0], scale: [0, 4, 8, 12] }}
                        transition={{ duration: 1.1, ease: "easeOut", times: [0, 0.15, 0.55, 1] }}
                        onAnimationComplete={() => setBurst(null)}
                        style={{
                            position: "fixed",
                            left: burst.x,
                            top: burst.y,
                            width: "40vmax",
                            height: "40vmax",
                            marginLeft: "-20vmax",
                            marginTop: "-20vmax",
                            pointerEvents: "none",
                            zIndex: 60,
                            background:
                                "radial-gradient(circle, rgba(255,236,180,0.9) 0%, rgba(212,175,55,0.55) 35%, rgba(212,175,55,0.0) 70%)",
                            mixBlendMode: "screen",
                            filter: "blur(1px)",
                        }}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
